import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  CoachFunction,
  FunctionCoach,
} from "../../../../../../../../../../api/services/program/models";
import {
  useGetCoachAssignmentFunction,
  useGetTMIndividualCoachAssignmentFunction,
  useGetTMLeaderCoachAssignmentFunction,
} from "../../../../../../../../../../api/services/program/programApis";
import { useGetUsersDetailByRoleMutation } from "../../../../../../../../../../api/services/user/userApi";
import {
  FormController,
  MuiButtonSecondary,
  MuiGrid,
  MuiSelect,
  MuiTypography,
  OptionsDataType,
} from "../../../../../../../../../../components";
import { ADMIN_ROLE_IDS } from "../../../../../../../../../../helpers/constants";
import { useGridContainer } from "../../../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import { programDeliverableCountrySelector } from "../../../../../../../../../../redux/slices/program/programSelector";
import { convertFunctionsOrCoachToDropdown } from "../../../../../../../../helper";
import { AddCoachAssignmentFormKeys } from "../../../../../../../../modelsAndSchemas/Models";
import { AllowNoneCoachCountryList } from "../../constants";
import { CoachAssignmentDetailsList } from "../../model";
import { coachAssignmentSiteCoreKeys } from "../../SiteCoreKeys";
import { addCoachAssignmentFunSchema } from "../../ValidationSchema";

interface AddCoachAssignmentFunctionProps {
  onAddFunctionCallback: (
    selectedFunction: CoachFunction,
    coach: FunctionCoach | undefined,
  ) => void;
  coachAssignList: CoachAssignmentDetailsList;
  isTMIndividualProgram?: boolean;
  isTMLeaderCoachingProgram?: boolean;
  onParentSubmitPressed?: boolean;
}

export const AddCoachAssignmentFunction = ({
  onAddFunctionCallback,
  coachAssignList,
  isTMIndividualProgram,
  isTMLeaderCoachingProgram,
  onParentSubmitPressed,
}: AddCoachAssignmentFunctionProps) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const { useFetchLabel, commonLabels, commonValidationMessages } =
    useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const deliverablesCountry = useSelector(programDeliverableCountrySelector);

  const allowNoneCoachSelection = Boolean(
    deliverablesCountry &&
      AllowNoneCoachCountryList.includes(deliverablesCountry),
  );

  const [state, setState] = useState<{
    options: OptionsDataType[];
    filteredOptions: OptionsDataType[];
  }>({
    options: [],
    filteredOptions: [],
  });

  const tmIndividual = useGetTMIndividualCoachAssignmentFunction();
  const regular = useGetCoachAssignmentFunction();
  const tmLeaderCoach = useGetTMLeaderCoachAssignmentFunction();

  let data: CoachFunction[] | undefined;

  if (isTMIndividualProgram) {
    data = tmIndividual.data;
  } else if (isTMLeaderCoachingProgram) {
    data = tmLeaderCoach.data;
  } else {
    data = regular.data;
  }

  const getLabelByKey = useFetchLabel(
    coachAssignmentSiteCoreKeys.coachAssignment,
  );

  const { mutateAsync: getCoachList, data: coachesList } =
    useGetUsersDetailByRoleMutation();

  useEffect(() => {
    if (
      errors.function?.type === "optionality" ||
      errors.coach?.type === "optionality"
    ) {
      clearErrors();
    }
  }, [onParentSubmitPressed]);

  useEffect(() => {
    getCoachList({
      roleIds: [
        ADMIN_ROLE_IDS.RESUME_COACH,
        ADMIN_ROLE_IDS.DELIVERY_COACH,
        ADMIN_ROLE_IDS.TEMPORARY_COACH_HELPDESK,
        ADMIN_ROLE_IDS.LEAD_CONSULTANT,
      ],
    });
  }, []);

  useEffect(() => {
    if (data?.length) {
      const formattedFunctionsOptions = convertFunctionsOrCoachToDropdown(data);
      setState({
        options: formattedFunctionsOptions,
        filteredOptions: formattedFunctionsOptions,
      });
    }
  }, [data]);

  const methods = useForm({
    mode: "all",
    resolver: yupResolver(
      addCoachAssignmentFunSchema(
        commonValidationMessages,
        !allowNoneCoachSelection,
      ),
    ),
  });

  useEffect(() => {
    if (state.options?.length) {
      if (coachAssignList?.length) {
        const assignedFunctionsName = coachAssignList.map(
          (item) => item.functionName,
        );
        const filteredOptions = state.options.filter(
          (option) => !assignedFunctionsName.includes(option.label),
        );
        setState((prevState) => ({
          ...prevState,
          filteredOptions: filteredOptions,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          filteredOptions: state.options,
        }));
      }
    }
  }, [coachAssignList?.length, state.options?.length]);

  const {
    reset,
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const formSubmit = async (formData: unknown) => {
    const isCoachAlreadyAdded = coachAssignList.find(
      (coach) => coach.coachId === (formData as { coach: string }).coach,
    );

    if (isCoachAlreadyAdded) {
      setError(AddCoachAssignmentFormKeys.coach, {
        type: "custom",
        message: getLabelByKey(
          coachAssignmentSiteCoreKeys.coachAlreadyAssigned,
        ),
      });
      return;
    }

    const selectedCoach = coachesList?.find(
      (coach: FunctionCoach) =>
        coach.id === (formData as { coach: string }).coach,
    ) as FunctionCoach;
    const selectedCoachFunction = data?.find(
      (fun) => fun.id === (formData as { function: string }).function,
    ) as CoachFunction;
    onAddFunctionCallback(selectedCoachFunction, selectedCoach);
    reset();
  };

  const coachDropdownOptions = useMemo(() => {
    let functionOptions: OptionsDataType[] = [];
    if (coachesList?.length) {
      functionOptions = convertFunctionsOrCoachToDropdown(
        coachesList as FunctionCoach[],
      );
    }
    if (allowNoneCoachSelection) {
      const noneOption: OptionsDataType = {
        label: commonLabels.selectPlaceHolder,
        value: "",
      };
      functionOptions.unshift(noneOption);
    }
    return functionOptions;
  }, [coachesList]);

  const onCoachChange = () => clearErrors(AddCoachAssignmentFormKeys.coach);

  return (
    <>
      <Box className="add-function-header">
        <MuiTypography variant="h3Bold">
          {getLabelByKey(
            coachAssignmentSiteCoreKeys.coachAssignmentAddFunction,
          )}
        </MuiTypography>
      </Box>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(formSubmit)}>
          <MuiGrid container className="add-function-container">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={AddCoachAssignmentFormKeys.function}
              >
                <MuiSelect
                  label={getLabelByKey(
                    coachAssignmentSiteCoreKeys.coachAssignmentFunctionDropDownLabel,
                  )}
                  options={state.filteredOptions}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={AddCoachAssignmentFormKeys.coach}
              >
                <MuiSelect
                  placeholder={commonLabels.selectPlaceHolder}
                  showSearch
                  searchPlaceholder={commonLabels.search}
                  label={getLabelByKey(
                    coachAssignmentSiteCoreKeys.coachAssignmentCoachTableHeading,
                  )}
                  options={coachDropdownOptions}
                  visibleDefaultSelectOption
                  requiredARIALabel={commonARIALabel.required}
                  onChange={onCoachChange}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={classNames(gridSizeMediumClass, "add-function-button")}
            >
              <MuiButtonSecondary type="submit" id={`add-function-btn-cancel`}>
                {commonLabels.add}
              </MuiButtonSecondary>
            </MuiGrid>
          </MuiGrid>
        </form>
      </FormProvider>
    </>
  );
};
